// MealSection.js — A single meal type section (Breakfast, Lunch, or Dinner) with horizontally scrollable recipe cards
import React from "react";
import { useTranslation } from "react-i18next";
import RecipeCard from "../../Recipes/RecipeCard";
import MealTypeIcon from "./MealTypeIcon";
import { getServingsForDays, isCookFor2Days } from "../utils/mealPlanHelpers";

/**
 * @param {Object} props
 * @param {string} props.mealType - "Breakfast" | "Lunch" | "Dinner"
 * @param {Array} props.meals - Recipes of this type
 * @param {React.Ref} props.scrollRef - Ref for horizontal scroll container
 * @param {Object} props.cookingStyle - Full cooking style object
 * @param {Object} props.recipeCostByTitle - Map of recipe title → consumed-only cost (number)
 * @param {string} props.currencySymbol - Currency symbol for the cost badge
 * @param {boolean} props.canShowControls - Whether controls are ready to show
 * @param {boolean} props.groceryOperationInProgress
 * @param {Object} props.imagesLoading - Map of recipe title → loading boolean
 * @param {Function} props.onRecipeClick
 * @param {Function} props.onDislikeRecipe
 * @param {Function} props.onRemoveAllClick
 * @param {boolean} props.isGeneratingAlternatives
 * @param {Function} props.onToggleFavorite
 * @param {Function} props.isMealFavorite
 * @param {boolean} props.isAdmin
 * @param {Function} props.onAddToPromo
 * @param {Object} props.promoLoadingByRecipeKey
 * @param {Object} props.promoAddedByRecipeKey
 */
function MealSection({
  mealType,
  meals,
  scrollRef,
  cookingStyle,
  recipeCostByTitle,
  currencySymbol,
  canShowControls,
  groceryOperationInProgress,
  imagesLoading,
  onRecipeClick,
  onDislikeRecipe,
  onRemoveAllClick,
  isGeneratingAlternatives,
  onToggleFavorite,
  isMealFavorite,
  isAdmin = false,
  onAddToPromo,
  promoLoadingByRecipeKey = {},
  promoAddedByRecipeKey = {},
}) {
  const { t } = useTranslation('mealplan');

  return (
    <div className="max-w-5xl mx-auto">
      <div className="pt-6 flex mb-3 px-6 justify-between items-center">
        {/* Left side: Title and Remove button */}
        <div className="flex items-center gap-2">
          <h3
            className="
                text-[var(--Color-Grays-Gray-10,#0D0C07)]
                text-[18px]
                font-bold
                leading-[32px]
                flex items-center
              "
          >
            <MealTypeIcon mealType={mealType} />
            <span className="ml-1.5">{t(`mealTypes.${mealType.toLowerCase()}`)}</span>
          </h3>
          {/* Remove All button */}
          {canShowControls && !groceryOperationInProgress && (
            <button
              className="font-semibold bg-[#F3F3F2] text-[12px] leading-[16px] border-[2px] border-[rgba(255,255,2555,1)] rounded-[6px] ml-1 px-2 h-[26px] shadow-[0_0px_0px_1px_rgba(212,11,11,0.32)]
                transition-colors text-[#4D4B42] md:hover:text-[#D40B0B] border-[#D40B0B]"
              onClick={() => onRemoveAllClick(mealType)}
            >
              {t('actions.remove')}
            </button>
          )}
          {groceryOperationInProgress && (
            <span className="font-semibold text-[12px] leading-[20px] text-[#A09F9C]">
              {t('status.updating')}
            </span>
          )}
        </div>
        {/* Right side: Meal count */}
        <span className="text-[#878683] text-[12.8px] font-normal">
          {t('mealCount', { count: meals.length })}{isCookFor2Days(cookingStyle, mealType.toLowerCase()) ? ` ${t('forTwoDays')}` : ''}
        </span>
      </div>

      <div
        ref={scrollRef}
        className="flex overflow-x-auto scrollbar-hide max-w-5xl mx-auto px-6 pb-4 border-[#EBEBEB] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-gray-100"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch", overscrollBehaviorX: "contain" }}
      >
        <div className="flex flex-nowrap gap-4">
          {meals.map((recipe, index) => {
            const isRecipeReady =
              !recipe.isPlaceholder &&
              !recipe.fetchingDetailsForImage &&
              !imagesLoading[recipe.title] &&
              recipe.imageURL;

            const isRecipeClickable =
              !recipe.isPlaceholder &&
              !recipe.fetchingDetailsForImage &&
              recipe.instructions;

            const servingsForDays = getServingsForDays(cookingStyle, recipe.mealType);

            return (
              <RecipeCard
                key={recipe.id || recipe.originalTitle || recipe.title || index}
                recipe={recipe}
                index={index}
                onRecipeClick={onRecipeClick}
                imagesLoading={imagesLoading}
                isRecipeReady={isRecipeReady}
                isRecipeClickable={isRecipeClickable}
                mealCost={recipeCostByTitle
                  ? (recipeCostByTitle[recipe.title] ?? recipeCostByTitle[recipe.originalTitle])
                  : undefined}
                currencySymbol={currencySymbol}
                onDislikeRecipe={onDislikeRecipe}
                groceryListLoaded={canShowControls}
                isGeneratingAlternatives={isGeneratingAlternatives}
                servingsForDays={servingsForDays}
                isFavorite={isMealFavorite ? isMealFavorite(recipe) : false}
                onToggleFavorite={onToggleFavorite}
                showPromoButton={isAdmin && !promoAddedByRecipeKey[recipe.id || recipe.originalTitle || recipe.title || index]}
                onAddToPromo={onAddToPromo}
                isAddingToPromo={!!promoLoadingByRecipeKey[recipe.id || recipe.originalTitle || recipe.title || index]}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MealSection;
